import { Card, cx } from '../../components/ui';
import { type QaRun } from '../../lib/api';
import { useLiveRuns } from '../../lib/liveUsage';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatDate } from './Credentials';
import { PanelState } from './DashboardLayout';

export function Usage() {
  usePageTitle('QA runs — WaveQA');

  // A run appears here the moment the agent opens metering and stays `running`
  // for the length of the verification, so this list is stale the whole time it
  // matters most. `useLiveRuns` refreshes it while anything is executing.
  const { data, isPending, error } = useLiveRuns();

  return (
    <div>
      <h2 className="text-lg font-semibold text-white">QA runs</h2>
      <p className="mt-1 max-w-xl text-sm text-hull-400">
        Summaries reported by your agents. Evidence and full reports stay on the
        machine that ran them — only these counts are uploaded.
      </p>

      <div className="mt-6">
        <PanelState
          loading={isPending}
          error={error}
          empty={data?.length === 0}
          emptyTitle="No runs yet"
          emptyBody="Run `waveqa -p &quot;YourProject&quot;` on a configured machine and the summary will appear here."
        >
          <ul className="space-y-3">
            {data?.map((run) => (
              <RunRow key={run.run_id} run={run} />
            ))}
          </ul>
        </PanelState>
      </div>
    </div>
  );
}

/**
 * The metered time a run consumed, in the units a person reads.
 *
 * This is the billed figure the server charged the plan, not a duration
 * recomputed from the timestamps beside it — a run's cost and the number shown
 * against it should not be two separate calculations that can disagree.
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function RunRow({ run }: { run: QaRun }) {
  const tone =
    {
      completed: 'text-pass-400 border-pass-600/40',
      failed: 'text-fail-400 border-fail-600/40',
      blocked: 'text-warn-400 border-warn-600/40',
      running: 'text-surge-400 border-surge-600/40',
      aborted: 'text-hull-400 border-hull-600',
    }[run.status] ?? 'text-hull-400 border-hull-600';

  const unverified =
    run.criteria_total -
    run.criteria_passed -
    run.criteria_failed -
    run.criteria_blocked;

  return (
    <Card as="li">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-white">{run.project_name}</h3>
            {run.feature_name && (
              <span className="text-sm text-hull-400">/ {run.feature_name}</span>
            )}
            <span className={cx('rounded-full border px-2 py-0.5 text-[11px]', tone)}>
              {run.status}
            </span>
          </div>

          <p className="mt-1.5 text-xs text-hull-400">
            {formatDate(run.started_at)}
            {run.billable_seconds !== null && ` · ${formatDuration(run.billable_seconds)}`}
            {run.agent_version && ` · agent ${run.agent_version}`}
            <span className="ml-2 font-mono">{run.run_id.slice(0, 12)}…</span>
          </p>
        </div>

        {run.criteria_total > 0 && (
          <div className="flex shrink-0 gap-4 text-sm">
            <Tally label="Passed" value={run.criteria_passed} tone="text-pass-400" />
            <Tally label="Failed" value={run.criteria_failed} tone="text-fail-400" />
            <Tally label="Blocked" value={run.criteria_blocked} tone="text-warn-400" />
            {/* Criteria that were neither proven nor disproven. Shown because a
                run with unverified criteria is not a clean pass. */}
            <Tally label="Unverified" value={unverified} tone="text-hull-400" />
          </div>
        )}
      </div>

      {run.criteria_total > 0 && (
        <div className="mt-4 flex h-1.5 overflow-hidden rounded-full bg-hull-700">
          <Bar value={run.criteria_passed} total={run.criteria_total} className="bg-pass-600" />
          <Bar value={run.criteria_failed} total={run.criteria_total} className="bg-fail-600" />
          <Bar value={run.criteria_blocked} total={run.criteria_total} className="bg-warn-600" />
        </div>
      )}
    </Card>
  );
}

function Tally({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="text-center">
      <p className={cx('text-lg font-semibold tabular-nums', value > 0 ? tone : 'text-hull-600')}>
        {value}
      </p>
      <p className="text-[11px] uppercase tracking-wide text-hull-500">{label}</p>
    </div>
  );
}

function Bar({ value, total, className }: { value: number; total: number; className: string }) {
  if (value === 0) return null;
  return <div className={className} style={{ width: `${(value / total) * 100}%` }} />;
}
